import React, { useRef } from 'react';
import { Col, Container, Row } from "react-bootstrap";
import config from "../../../contexts/ConfigContext";
import { t } from 'i18next';
import DataGridCustom, { ColumnCustomProps, DataGridHandle } from 'components/DataGridCustom';
import { GridColumnMenuFilter, GridColumnMenuProps } from '@progress/kendo-react-grid';
import { ODataProvider } from 'contexts/ODataContext';
import { Icon, SvgIcon } from '@progress/kendo-react-common';
import moment from 'moment';
import { Window } from '@progress/kendo-react-dialogs';
import { DateInput, DateRangePicker, DateRangePickerChangeEvent, SelectionRange } from '@progress/kendo-react-dateinputs';
import { ImportSection } from 'components/Configuration/Project/ImportSection';
import { ExportSection } from 'components/Configuration/Project/ExportSection';
import { Trans } from 'react-i18next';
import { CommonLibrary } from 'contexts/CommonContext';
import { ProjectModel } from '@launchmetrics/dmr-header-component/src/Model/project-model';
import { Button } from '@progress/kendo-react-buttons';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import * as svgIcons from "@progress/kendo-svg-icons";


interface DataflowProjectHistoryProps {
    type: number;
}

function DataflowProjectHistory({ type }: DataflowProjectHistoryProps) {    
    const userApiUrl = config.API_URL + "/odata/ProjectDataflowHistories";
    const dataGridRef = useRef<DataGridHandle>(null);
    const comonLibrary = new CommonLibrary();
    const [selectedProject] = React.useState<ProjectModel | undefined>(comonLibrary.ActiveProject);

  // Define the column menu
  const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} />
      </div>
    );
  };


  React.useEffect(() => {   
    dataGridRef.current?.filterData({
      logic: "and",
      filters: [
      {
        field: "TypeId",
        operator: "eq",
        value: type
      }
      ]
    });

    dataGridRef.current?.orderData([
      {
        field: "StartDate",
        dir: "desc"
      }
    ]);
  }, []);
    
    const [selectedRow, setSelectedRow] = React.useState<any>(null);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);

    const handleRowClick = (dataItem: any) => {
      setSelectedRow(dataItem);
      setIsDialogOpen(true);
    };

    const commandCell = (props: any) => (
      <td style={{ textAlign: "center" }}>
      <span className="button-grid">
        <Button
        themeColor="primary"
        onClick={() => handleRowClick(props.dataItem)}
        >
        <Trans>configuration.projects.details</Trans>
        </Button>
      </span>
      </td>
    );

    const handleDialogClose = () => {
      setIsDialogOpen(false);
      setSelectedRow(null);
    };

    const CustomStartDateInput = (props: any) => {
      return (
        <label style={{ textAlign: "center" }}>
          <DateInput {...props} label={undefined} placeholder={t("general.start")}  value={props.start} />
        </label>
      );
    };

    const CustomEndDateInput = (props: any) => {
      return (
        <label style={{ textAlign: "center" }}>
          <DateInput {...props} label={undefined} placeholder={t("general.end")} value={props.end}/>
        </label>
      );
    };
    const customDateCell = (props: any) => {
      if (props.dataItem[props.field]) {
        return (
          <td>
            <Moment format="YYYY/MM/DD HH:mm">{props.dataItem[props.field]}</Moment>
          </td>
        );
      } else {
        return <td>-</td>; // Display a placeholder or empty cell if the value is null or undefined
      }
    };

    const [valueDateRange, setValueDateRange] = React.useState<SelectionRange>({
        start: sessionStorage.getItem("startDate") ? new Date(sessionStorage.getItem("startDate") as string) : new Date(new Date().setDate(new Date().getDate() - 30)),
        end: sessionStorage.getItem("endDate")? new Date(sessionStorage.getItem("endDate") as string):new Date(),
      });
      
    const handleDateRangeChange = (event: DateRangePickerChangeEvent) => {
      setValueDateRange(event.value);
    };
    
    const handleApplyFilter = () => {
      if (dataGridRef.current) {
        const startDate = valueDateRange?.start ? moment(valueDateRange.start).startOf('day').toISOString() : null;
        const endDate = valueDateRange?.end ? moment(valueDateRange.end).endOf('day').toISOString() : null;

        dataGridRef.current.filterData({
          logic: "and",
          filters: [
          {
            field: "TypeId",
            operator: "eq",
            value: type
          },{
            field: "StartDate",
            operator: "ge",
            value: moment(startDate).startOf('day').toDate()
          }
          ,{
            field: "EndDate",
            operator: "le",
            value:  moment(endDate).startOf('day').toDate() 
          }
          ]
        });

        // Save the selected range in sessionStorage
        if (startDate) {
          sessionStorage.setItem("startDate", startDate);
        } else {
          sessionStorage.removeItem("startDate");
        }

        if (endDate) {
          sessionStorage.setItem("endDate", endDate);
        } else {
          sessionStorage.removeItem("endDate");
        }
      }
    };
    const columns = [] as ColumnCustomProps[];
    
    columns.push({ 
      field: "StartDate", 
      title: t("general.start"), 
      columnMenu: undefined, 
      isExportable: true, 
      filter: "date",
      filterable: false,
      cell: customDateCell
    });  

    columns.push({ 
      field: "EndDate", 
      title: t("general.end"), 
      columnMenu: undefined, 
      filter: "date",
      filterable: false,
      isExportable: true, 
      cell: customDateCell
      
    });  
    columns.push({ 
      field: "Status", 
      title: t("general.status"), 
      columnMenu: ColumnMenu, 
      isExportable: true, 
      cell: (props: any) => {
      return (
        <td>
        {props.dataItem.Status && <Trans>{`general.${props.dataItem.Status}`}</Trans>}
        </td>
      );
      } 
    });  
    columns.push({
        field: "Messages",
        title: t("configuration.projects.messages"),
        columnMenu: undefined,
        isExportable: true,
        width: "100px", // Set a smaller width for the column
        cell: (props: any) => {
            const details = props.dataItem.Details || [];
            const logs = details.flatMap((detail: any) => detail.Logs || []);
            
            if (logs.length === 0) {
                return <td style={{ textAlign: 'center' }}></td>;
            }

            let iconName = 'info';
            let color = 'blue';

            if (logs.some((log: any) => log.severity_id === 2)) { // Assuming 2 represents 'Warning'
                iconName = 'warning';
                color = 'orange';
            } else if (logs.every((log: any) => log.severity_id === 1)) { // Assuming 1 represents 'Low'
                iconName = 'info';
                color = 'blue';
            }

            return (
                <td style={{ color, textAlign: 'center' }}>
                    <Icon name={iconName} style={{ color, width: '20px', height: '20px' }} />
                </td>
            );
        }
    });
    columns.push({ cell: commandCell, width: "200px", isExportable: false });


    return (
      <>
        <Col className="g-4 px-4" style={{ padding: "20px" }}>
          <Row className="border g-4 px-4 m-4 rounded tile-content py-3 my-4 d-flex flex-column ">
            <Container >
              <div>
              <Row className="filter-content">
                <Col md={10}>
                  <h5 className="title">                
                  {selectedProject?.Description}{" "}
                  {type === 0 ? <Trans>configuration.projects.projectImports</Trans> : <Trans>configuration.projects.projectExports</Trans>}
                  </h5>
                </Col>
                <Col md={2}>
                  <div className="space-between">
                    <Link className="btn" to="/DataLabeling/project" onClick={(e) => {
                      e.preventDefault();
                      window.location.href = "/DataLabeling/project";
                    }}>
                      <SvgIcon className="btn-icons" icon={svgIcons.arrowLeftIcon} size="medium" /><Trans>general.returnToBack</Trans>
                    </Link>
                  </div>
                </Col>
              </Row>
              <Row className="filter-content">
                <Col md={1} className="pt-1">
                <span><Trans>general.dateFilter</Trans></span>
                </Col>
                <Col md={4}>
                <form className="k-form" onSubmit={(e) => e.preventDefault()}>
                  <DateRangePicker
                  clearButton={true}
                  format={"dd-MMM-yyyy"}
                  startDateInput={(props) => (
                    <CustomStartDateInput {...props} {...valueDateRange} />
                  )}
                  endDateInput={(props) => (
                    <CustomEndDateInput {...props} {...valueDateRange} />
                  )}
                  onChange={handleDateRangeChange}
                  
                  />
                </form>
                </Col>
                <Col md={2}>
                <div className="float-right">
                  <Button 
                  title={t("general.apply")} 
                  themeColor={"primary"} 
                  type="button" 
                  onClick={handleApplyFilter}
                  >
                  <Trans>general.apply</Trans>
                  </Button>
                </div>
                </Col>
              </Row>
              </div>
            </Container>
          </Row>
          <Row className="row g-4 m-4 px-4 tile-content border rounded" style={{ marginTop: '4rem' }} >
            <ODataProvider baseUrl={userApiUrl} loadingContent=".k-grid-content">
            <DataGridCustom 
              autoBind={false} 
              PermissionButtonCode="menu_projects" 
              ref={dataGridRef} 
              DataItemKey="DataflowId" 
              Columns={columns} 
            />
            </ODataProvider>
          </Row>

          {isDialogOpen && (
            <Window
            title={t("configuration.projects.details")}
            onClose={handleDialogClose}
            initialWidth={800}
            initialHeight={600}
            modal={true}
            >
            <div style={{ height: '100%' }}>
              {selectedRow && type === 0 && (
              <ImportSection
                importData={selectedRow}
                showHistoryButton={false}
                fromList={true}
              />
              )}

              {selectedRow && type === 1 && (
              <ExportSection
                exportData={selectedRow}
                showHistoryButton={false}
                fromList={true}
              />
              )}
            </div>
            </Window>
          )}
        </Col>
      </>
    );
};

export default DataflowProjectHistory;